import { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import {
  DepositForm,
  PortfolioChart,
  PortfolioValueHeader,
  PortfolioValueHeaderSkeleton,
  WalletActivityList,
  WithdrawForm,
  type PortfolioTimeframe,
} from '@/components/features';
import { useOtpSlot } from '@/components/features/Otp';
import { Skeleton } from '@/components/ui';
import {
  useCashBackGetMine,
  useDepositList,
  useTradingPositionGetPnl,
  useTradingPositionGetPortfolio,
  useWithdrawalGetBalance,
  useWithdrawalList,
} from '@/lib/endpoints';
import { TradingPositionGetPnlPeriod } from '@/lib/models';
import { formatCrownDollars } from '@/lib/monetization';
import { cn } from '@/lib/utils';

type WalletTab = 'deposit' | 'withdraw' | 'activity';

/** Chart pill → PnL query period. The series is daily, so there is no 1D pill. */
const PNL_PERIOD: Record<PortfolioTimeframe, TradingPositionGetPnlPeriod> = {
  '1W': TradingPositionGetPnlPeriod['1W'],
  '1M': TradingPositionGetPnlPeriod['1M'],
  All: TradingPositionGetPnlPeriod.ALL,
};

/**
 * `/wallet` route, structured as the Portfolio page's sibling: on desktop,
 * one wide hero card — the shared gradient money hero
 * ({@link PortfolioValueHeader}) with the realized-P&L chart
 * ({@link PortfolioChart}) as its lower half. On mobile the hero is hidden
 * (the app-shell top bar already carries the compact balance), so the page
 * starts at the underline tab strip:
 * - **Deposit** / **Withdraw** — the cashier forms, inline in a centered column.
 * - **Activity** — the deposits/withdrawals statement.
 *
 * The hero's delta line is the unrealized P&L of open positions (the same
 * figure the Portfolio hero tracks).
 *
 * Forms mount only while their tab is active (DepositForm mints a Pay.com
 * intent on mount), which is why tab content is branched in JS, never
 * CSS-hidden. Deep links: `?tab=deposit|withdraw|activity`.
 */
export function WalletPage() {
  const [searchParams] = useSearchParams();
  const tabParam = searchParams.get('tab');
  const [tab, setTab] = useState<WalletTab>(
    tabParam === 'withdraw' ? 'withdraw' : tabParam === 'activity' ? 'activity' : 'deposit',
  );
  const [timeframe, setTimeframe] = useState<PortfolioTimeframe>('All');

  // Register the OTP slot at the page level (not inside WithdrawForm) so the
  // global <OtpModal> stays suppressed even when the user switches away from
  // the withdraw flow while an OTP challenge is open — otherwise WithdrawForm
  // would unmount, the slot would deregister, and the OTP would pop up as the
  // modal.
  useOtpSlot();

  // Same live-updating query the side nav uses (patched by useWalletBalanceRealtime).
  const balanceQuery = useWithdrawalGetBalance();
  const portfolioQuery = useTradingPositionGetPortfolio();
  const cashBackQuery = useCashBackGetMine();
  const { data: pnlPoints = [], isLoading: pnlLoading } = useTradingPositionGetPnl({ period: PNL_PERIOD[timeframe] });
  // The Activity tab's count — the same queries the list itself uses, so the
  // react-query cache is shared and nothing fetches twice.
  const depositsQuery = useDepositList();
  const withdrawalsQuery = useWithdrawalList();

  const portfolio = portfolioQuery.data;
  const balance = balanceQuery.data?.balance ?? 0;
  const delta = portfolio?.totalUnrealizedPnl ?? 0;
  const deltaPercentage = portfolio && portfolio.totalCost > 0 ? (delta / portfolio.totalCost) * 100 : 0;
  const positionCount = portfolio?.positionCount ?? 0;
  const crownDollarsCents = cashBackQuery.data?.balance.balanceCents ?? 0;
  const activityCount = (depositsQuery.data?.length ?? 0) + (withdrawalsQuery.data?.length ?? 0);

  // Gate the hero on all three queries: these are real-money figures, and a
  // fast balance response must not paint P&L or Crown Dollars as $0 while
  // their queries are still in flight.
  const heroLoading = balanceQuery.isLoading || portfolioQuery.isLoading || cashBackQuery.isLoading;

  return (
    <div className="flex flex-1 flex-col pb-[calc(82px+env(safe-area-inset-bottom))] md:pb-0">
      <div className="flex flex-col gap-4 px-4 md:px-6 lg:px-8 xl:px-10 w-full max-w-[600px] lg:max-w-[1300px] mx-auto">
        {/* Hero card — desktop only (the mobile top bar already carries the
            compact balance); the treatment mirrors the Portfolio page's
            header+chart card. */}
        <div className="hidden lg:flex flex-col gap-6 lg:rounded-card lg:border lg:border-border lg:bg-surface lg:p-6 lg:shadow-card">
          {heroLoading ? (
            // Mirrors the loaded hero — value header + Crown Dollars line on
            // the left, then the chart area with its timeframe pills — so the
            // card doesn't grow when the real figures land.
            <>
              <div className="flex flex-col items-start gap-2">
                <PortfolioValueHeaderSkeleton />
                <Skeleton className="h-4 w-64 rounded" />
              </div>
              <div className="flex flex-col gap-3 p-3">
                <Skeleton className="h-[180px] w-full rounded-[10px] lg:h-[260px]" />
                <Skeleton className="h-9 w-36 self-center rounded-pill" />
              </div>
            </>
          ) : (
            <>
              <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between">
                <div className="flex flex-col items-start">
                  <PortfolioValueHeader
                    label="Cash Balance"
                    value={balance}
                    fractionDigits={2}
                    delta={delta}
                    deltaPercentage={deltaPercentage}
                    deltaSuffix={`Unrealized · ${positionCount} open ${positionCount === 1 ? 'position' : 'positions'}`}
                  />
                  <p className="text-sm text-muted">
                    <span className="font-semibold text-gold">◎ {formatCrownDollars(crownDollarsCents)} Crown Dollars</span>{' '}
                    ready to spend
                  </p>
                </div>
                <Link to="/portfolio" className="text-sm font-semibold text-purple hover:underline">
                  View portfolio
                </Link>
              </div>

              {/* Lower half of the card — the same realized-P&L chart the
                  Portfolio page shows, chrome-less inside the card. */}
              <PortfolioChart
                points={pnlPoints}
                timeframe={timeframe}
                onTimeframeChange={setTimeframe}
                isLoading={pnlLoading}
                className="border-0 bg-transparent p-0 shadow-none"
              />
            </>
          )}
        </div>

        <WalletTabs activeTab={tab} onTabChange={setTab} activityCount={activityCount} />

        {(tab === 'deposit' || tab === 'withdraw') && (
          <div className="flex w-full max-w-[600px] mx-auto flex-col gap-5 pb-4 lg:gap-6">
            {tab === 'deposit' ? <DepositForm /> : <WithdrawForm />}
          </div>
        )}
        {tab === 'activity' && (
          <div className="pb-4">
            <WalletActivityList onDeposit={() => setTab('deposit')} />
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * Underline tab strip — mirrors {@link PortfolioTabs} px-for-px so the two
 * money pages share one tab style (only the tab set differs).
 */
function WalletTabs({
  activeTab,
  onTabChange,
  activityCount,
}: {
  activeTab: WalletTab;
  onTabChange: (tab: WalletTab) => void;
  activityCount?: number;
}) {
  const tabs: { id: WalletTab; label: string; count?: number }[] = [
    { id: 'deposit', label: 'Deposit' },
    { id: 'withdraw', label: 'Withdraw' },
    { id: 'activity', label: 'Activity', count: activityCount },
  ];

  return (
    <div role="tablist" aria-label="Wallet sections" className="flex w-full">
      {tabs.map((t) => {
        const isActive = t.id === activeTab;
        return (
          <button
            key={t.id}
            role="tab"
            type="button"
            aria-selected={isActive}
            onClick={() => onTabChange(t.id)}
            className={cn(
              'flex-1 flex items-center justify-center gap-1 py-2 text-sm font-sans cursor-pointer transition-colors whitespace-nowrap',
              'border-b-2',
              isActive
                ? 'font-semibold text-purple border-purple'
                : 'font-normal text-ink-muted border-ink-muted/50 hover:text-primary',
            )}
          >
            <span>{t.label}</span>
            {typeof t.count === 'number' && t.count > 0 && <span>({t.count})</span>}
          </button>
        );
      })}
    </div>
  );
}
